import {
  Box,
  Link,
  List,
  Stack,
  Divider,
  ListItem,
  MenuItem,
  Typography,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import Head from "next/head";
import { TbShirtSport } from "react-icons/tb";
import { MdOutlinedFlag } from "react-icons/md";
import { HiOutlineTicket } from "react-icons/hi";
import { OpenInNewRounded } from "@mui/icons-material";

import constants from "@/global/config/constants";
import { createState } from "@/global/store/zustand_store";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";
import { MLBAdobeScriptHeader } from "@/global/common/mlb-common/MLBAdobeScriptHeader";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

export const NewYorkYankeesLink = () => (
  <Link
    target="_blank"
    sx={{
      fontWeight: 500,
      color: "primary.main",
      textDecoration: "none",
    }}
    href="https://am.ticketmaster.com/nyyreg/"
  >
    Ballpark App
  </Link>
);

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose your games.",
    description: "Select the games you wish to attend.",
  },

  {
    icon: <TbShirtSport />,
    title: "Select Your Seats",
    description: "Select the seats you wish to purchase for each game.",
  },

  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description: "Tickets will be available in your Ballpark App.",
  },
];

export const faqs = [
  {
    title: "When will I receive my tickets?",
    description:
      "If your reservation is accepted, a New York Yankees representative will be in contact with further information, including ticket delivery details.",
  },
  {
    title: "What happens after I purchase a New York Yankees Suite online?",
    description:
      "Following your purchase, a New York Yankees representative will follow up with you to confirm your order and assist with any questions you have in anticipation of your event. Tickets and parking will be added to your New York Yankees account where you'll be able to access them via the MLB Ballpark app. All suite tickets are digital.",
  },
  {
    title: "What is included in my suite?",
    description:
      "If your reservation is accepted, a New York Yankees representative will be in contact with further information, including details regarding parking and food and beverage options.",
  },
  {
    title:
      "Am I guaranteed a promotional item if I purchase tickets to that game?",
    description: (
      <>
        Purchasing a ticket for any promotional date does not guarantee that a
        Guest will receive the designated giveaway item. Distribution of
        promotional items is for Guests in attendance only, while supplies last.
        All promotional dates, items and distribution are subject to change
        and/or cancellation. For complete details on giveaway items and event
        dates, please review the{" "}
        <Link
          href="https://www.mlb.com/yankees/tickets/promotions/schedule"
          target="_blank"
        >
          full Yankees Promotional Schedule
        </Link>{" "}
        and{" "}
        <Link href="https://www.mlb.com/yankees/tickets/promotions/schedule#guidelines">
          Guidelines for Yankee Stadium Promotional Item Distribution
        </Link>
        .
      </>
    ),
  },
];

export function NewYorkYankeesCheckoutTerms() {
  return (
    <>
      In connection with your purchase, you will receive ticket-related
      commercial emails from{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="http://yankees.com/"
      >
        yankees.com
      </Link>
      . You may unsubscribe at any time. By checking this box you agree to
      receive additional commercial emails from{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="http://yankees.com/"
      >
        yankees.com
      </Link>{" "}
      and{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/"
      >
        MLB.com
      </Link>
      . The terms and conditions of your ticket and policies applicable to its
      use are updated to address circumstances related to COVID-19 and health
      and safety. Further, due to the COVID-19 pandemic, scheduling may be
      impacted; games may be canceled, rescheduled, or postponed or games may be
      held with limited capacity. Upon completing your purchase by selecting
      Purchase Tickets you acknowledge and agree that your credit card will be
      charged. By clicking “Purchase Tickets”, you agree to the{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/app/ballpark/official-information/privacy-policy"
      >
        MLB Privacy Policy
      </Link>
      ,{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/app/ballpark/official-information/terms-of-use"
      >
        Terms of Use
      </Link>{" "}
      and to receive your ticket(s) via an SMS text message to the mobile #
      associated with your Yankees account. Message and data rates may apply
      Tickets will be made available in the MLB Ballpark mobile application
      only.{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/"
      >
        MLB.com
      </Link>{" "}
      and supported mobile device required.
    </>
  );
}

export const NewYorkYankeesAdditionalDetailsDisclaimerTC = () => (
  <>
    By entering my name, checking the box above and clicking Submit Request, I
    hereby (i) agree to the{" "}
    <Link
      target="_blank"
      href="https://www.mlb.com/yankees/tickets/terms-and-conditions"
    >
      Terms of Use
    </Link>{" "}
    (ii) agree that my reservation will not be complete unless and until I
    receive a notice from the New York Yankees confirming whether or not my
    reservation has been accepted in accordance with our agreement.
  </>
);

export const NewYorkYankeesTCListItems = () => (
  <Box>
    <MenuItem
      onClick={() => {
        window.open(
          "https://www.mlb.com/yankees/tickets/terms-and-conditions",
          "_blank"
        );
      }}
    >
      <ListItemIcon>
        <OpenInNewRounded fontSize="small" />
      </ListItemIcon>
      <ListItemText>New York Yankees</ListItemText>
    </MenuItem>

    <MenuItem
      onClick={() => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          "_blank"
        );
      }}
    >
      <ListItemIcon>
        <OpenInNewRounded fontSize="small" />
      </ListItemIcon>
      <ListItemText>MLB Privacy Policy</ListItemText>
    </MenuItem>

    <MenuItem
      onClick={() => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          "_blank"
        );
      }}
    >
      <ListItemIcon>
        <OpenInNewRounded fontSize="small" />
      </ListItemIcon>
      <ListItemText>MLB Terms of Use</ListItemText>
    </MenuItem>
  </Box>
);

export function AnalyticsHeader(props: { teamConfig: TeamConfig }) {
  const { teamSchema } = props.teamConfig;

  return (
    <>
      <MLBAdobeScriptHeader />

      <Head>
        <script
          nonce={process.env.pixelScriptNonce}
          id={`${teamSchema}-meta-pixel-script`}
          defer
          dangerouslySetInnerHTML={{
            __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '553992158036687');
              fbq('track', 'PageView');
              console.log('meta pixel page load event fired.');
              window.addEventListener("${constants.customEvents.eventPurchaseSuccess}", function(e) {
                try {
                  const { cartRes, checkoutRes } = e.detail;
                  fbq('track', 'Purchase', {
                    content_name: "${teamSchema}_event_purchase", 
                    value: String(cartRes.allInPrice), 
                    currency: 'USD', 
                    order_id: checkoutRes.orderNumber,
                  });
                  console.log('meta pixel purchase event fired.');
                } catch (error) {
                  console.log('Error in meta pixel purchase event', error); 
                }
              });
            `,
          }}
        />

        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src="https://www.facebook.com/tr?id=553992158036687&ev=PageView&noscript=1"
          />
        </noscript>

        {/** tiktok script */}
        <script
          nonce={process.env.tiktokScriptNonce}
          defer
          id={`${teamSchema}-tik-tok-script`}
          dangerouslySetInnerHTML={{
            __html: `
              !(function (w, d, t) {
                w.TiktokAnalyticsObject = t;
                var ttq = (w[t] = w[t] || []);
                (ttq.methods = [
                  "page",
                  "track",
                  "identify",
                  "instances",
                  "debug",
                  "on",
                  "off",
                  "once",
                  "ready",
                  "alias",
                  "group",
                  "enableCookie",
                  "disableCookie",
                ]),
                  (ttq.setAndDefer = function (t, e) {
                    t[e] = function () {
                      t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
                    };
                  });
                for (var i = 0; i < ttq.methods.length; i++)
                  ttq.setAndDefer(ttq, ttq.methods[i]);
                (ttq.instance = function (t) {
                  for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++)
                    ttq.setAndDefer(e, ttq.methods[n]);
                  return e;
                }),
                  (ttq.load = function (e, n) {
                    var i = "https://analytics.tiktok.com/i18n/pixel/events.js";
                    (ttq._i = ttq._i || {}),
                      (ttq._i[e] = []),
                      (ttq._i[e]._u = i),
                      (ttq._t = ttq._t || {}),
                      (ttq._t[e] = +new Date()),
                      (ttq._o = ttq._o || {}),
                      (ttq._o[e] = n || {});
                    n = document.createElement("script");
                    (n.type = "text/javascript"),
                      (n.async = !0),
                      (n.src = i + "?sdkid=" + e + "&lib=" + t);
                    e = document.getElementsByTagName("script")[0];
                    e.parentNode.insertBefore(n, e);
                  });

                ttq.load("CC3QFFRC77U2GLNDVGG0");
                ttq.page();
                console.log("tiktok pixel page view fired.");
              })(window, document, "ttq");

              window.addEventListener(
                "${constants.customEvents.eventPurchaseSuccess}",
                async function (e) {
                  var cartRes = e && e.detail && e.detail.cartRes;
                  var checkoutRes = e && e.detail && e.detail.checkoutRes;

                  ttq.identify({
                    email: e.detail.user.email,
                    phone_number: "+1" + e.detail.user.phoneNumber,
                  });

                  ttq.track("CompletePayment", {
                    content_id: checkoutRes.orderNumber,
                    content_name: "${teamSchema}_event_purchase",
                    quantity: String(cartRes.totalSeats),
                    price: String(cartRes.allInPrice),
                    value: String(cartRes.allInPrice),
                    currency: "USD",
                  });
                  console.log("tik tok pixel purchase event fired.");
                }
              );
            `,
          }}
        />
      </Head>
    </>
  );
}

export const LuxurySuitesContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Luxury Suites
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Private Viewing Space</b>: Can accommodate 16-34 Guests (capacity
          varies across suites and games).
        </Typography>
      </ListItem>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Food and Beverage Offerings</b>: In addition to all-inclusive
          ballpark fare (hot dogs, chicken tenders, pizza, Dingers, popcorn,
          chips, cookies, brownies) and nonalcoholic beverages, beer and Bud
          Light Seltzers are included with all Luxury Suites.* Ability to
          purchase upgraded menu through Legends Hospitality.
        </Typography>
      </ListItem>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Parking</b>: Each Luxury Suite includes two (2) complimentary
          parking passes.
        </Typography>
      </ListItem>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Added Bonus</b>: Receive a complimentary scoreboard message.**
        </Typography>
      </ListItem>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Enhanced Viewing Experience</b>: Luxury Suites feature exterior
          cushioned suite seating and a climate-controlled interior space with a
          private restroom.
        </Typography>
      </ListItem>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>3+ Game Suite Packs & Leases</b>: For significant savings,
          Customizable Multi-Game Luxury Suites Packages are available when
          purchasing for three (3) or more games.
        </Typography>
      </ListItem>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Add-ons</b>: Special add-ons like Hands on History and pre-game
          event spaces available through Yankee Stadium Events (subject to
          availability).
        </Typography>
      </ListItem>
    </List>

    <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
      *Guests must be 21 years of age or older to purchase or consume alcohol.
      Proper identification is required of all Guests and must be presented,
      regardless of age, to purchase any alcoholic beverage.
    </Typography>
    <Typography variant="body2" sx={{ fontSize: "0.875rem", mt: 1 }}>
      **Scoreboard messages are subject to availability.
    </Typography>
  </Box>
);

export const PartySuitesContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Party Suites
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Group-Friendly Space</b>: Can accommodate 38-80 Guests (capacity
          varies across suites and games). Ability to combine suites for larger
          capacities based on availability.
        </Typography>
      </ListItem>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Food and Beverage Offerings</b>: In addition to all-inclusive
          ballpark fare (hot dogs, chicken tenders, pizza, Dingers, popcorn,
          chips, cookies, brownies) and nonalcoholic beverages, beer and Bud
          Light Seltzers are included with all Party Suites.* Ability to
          purchase upgraded menu through Legends Hospitality.
        </Typography>
      </ListItem>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Added Bonus</b>: Receive a complimentary scoreboard message.**
        </Typography>
      </ListItem>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Add-ons</b>: Special add-ons like Hands on History and pre-game
          event spaces available through Yankee Stadium Events (subject to
          availability).
        </Typography>
      </ListItem>
    </List>

    <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
      *Guests must be 21 years of age or older to purchase or consume alcohol.
      Proper identification is required of all Guests and must be presented,
      regardless of age, to purchase any alcoholic beverage.
    </Typography>
    <Typography variant="body2" sx={{ fontSize: "0.875rem", mt: 1 }}>
      **Scoreboard messages are subject to availability.
    </Typography>
  </Box>
);

export const TheClubSuitesContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        The Club Suite
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Private Viewing Space</b>: Can accommodate 45-100 Guests
          (capacity varies across games).
        </Typography>
      </ListItem>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Food and Beverage Offerings</b>: In addition to all-inclusive
          ballpark fare (hot dogs, chicken tenders, pizza, Dingers, popcorn,
          chips, cookies, brownies) and nonalcoholic beverages, beer and Bud
          Light Seltzers are included with The Club Suite.* Ability to purchase
          upgraded menu through Legends Hospitality.
        </Typography>
      </ListItem>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Parking</b>: The Club Suite includes four (4) complimentary
          parking passes.
        </Typography>
      </ListItem>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Added Bonus</b>: Receive a complimentary scoreboard message.**
        </Typography>
      </ListItem>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Enhanced Viewing Experience</b>: The Club Suite features exterior
          cushioned suite seating and a climate-controlled interior space with
          private restrooms.
        </Typography>
      </ListItem>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Add-ons</b>: Special add-ons like Hands on History and pre-game
          event spaces available through Yankee Stadium Events (subject to
          availability).
        </Typography>
      </ListItem>
    </List>

    <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
      *Guests must be 21 years of age or older to purchase or consume alcohol.
      Proper identification is required of all Guests and must be presented,
      regardless of age, to purchase any alcoholic beverage.
    </Typography>
    <Typography variant="body2" sx={{ fontSize: "0.875rem", mt: 1 }}>
      **Scoreboard messages are subject to availability.
    </Typography>
  </Box>
);

export const inventoryCardSuiteModal = createState({
  title: "",
  isOpen: false,
  content: <></>,
});

export const NewYorkYankeesInventoryCard = () => {
  const modal = inventoryCardSuiteModal();
  const { md } = useCssMediaQuery("down");

  return (
    <NotificationModal
      hideTitleIcon
      hidePrimaryButton
      title={modal.title}
      hideSecondaryButton
      isOpen={modal.isOpen}
      renderFooter={() => null}
      onClose={() => modal.set({ isOpen: false })}
      paperProps={{ style: { marginLeft: 2, marginRight: 2 } }}
      renderBody={() => <Box p={1}>{modal.content}</Box>}
    />
  );
};

export const inventoryCardSuitViewMenueModal = createState({
  isOpen: false,
});

export const NewYorkYankeesInventoryCardViewMenu = () => {
  const { md } = useCssMediaQuery("down");
  const modal = inventoryCardSuitViewMenueModal();
  const iframeUrl = `https://drive.google.com/viewerng/viewer?embedded=true&url=${encodeURIComponent(
    "https://ss-leagues.s3.amazonaws.com/MLB/NY%20Yankees%20Suites%20Assets/2025%20Packages%20-%20IGS-Party-%20V4.pdf"
  )}`;

  return (
    <NotificationModal
      hideTitleIcon
      hidePrimaryButton
      title={"View Menu"}
      hideSecondaryButton
      isOpen={modal.isOpen}
      renderFooter={() => null}
      onClose={() => modal.set({ isOpen: false })}
      paperProps={{
        style: {
          marginLeft: 2,
          marginRight: 2,
          width: "100%",
          height: "100%",
          maxWidth: md ? "95vw" : "80vw",
        },
      }}
      bodyProps={{
        style: {
          height: "100%",
        },
      }}
      renderBody={() => (
        <Box sx={{ height: "100%", maxHeight: "85vh" }}>
          <iframe
            className="pdf-iframe"
            src={iframeUrl}
            width="100%"
            height={"100%"}
          ></iframe>
        </Box>
      )}
    />
  );
};

export function NewYorkYankeesRegistrationTerms() {
  return (
    <>
      In connection with your registration, you will receive ticket-related
      commercial emails from{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="http://yankees.com/"
      >
        yankees.com
      </Link>
      . You may unsubscribe at any time. By checking this box you agree to
      receive additional commercial emails from{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="http://yankees.com/"
      >
        yankees.com
      </Link>{" "}
      and{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/"
      >
        MLB.com
      </Link>
      . The terms and conditions of your ticket and policies applicable to its
      use are updated to address circumstances related to COVID-19 and health
      and safety. Further, due to the COVID-19 pandemic, scheduling may be
      impacted; games may be canceled, rescheduled, or postponed or games may be
      held with limited capacity. Upon completing your purchase by selecting
      Purchase Tickets you acknowledge and agree that your credit card will be
      charged. By clicking “Purchase Tickets”, you agree to the{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/app/ballpark/official-information/privacy-policy"
      >
        MLB Privacy Policy
      </Link>
      ,{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/app/ballpark/official-information/terms-of-use"
      >
        Terms of Use
      </Link>{" "}
      and to receive your ticket(s) via an SMS text message to the mobile #
      associated with your Yankees account. Message and data rates may apply
      Tickets will be made available in the MLB Ballpark mobile application
      only.{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/"
      >
        MLB.com
      </Link>{" "}
      and supported mobile device required.
    </>
  );
}
